import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function Checkout() {
  const { cart, refresh } = useCart();
  const { user, refresh: refreshUser } = useAuth();
  const { t, lang } = useLang();
  const [addr, setAddr] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (!user) return null;

  const insufficient = user.wallet_balance < cart.total;

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", { shipping_address: addr });
      toast.success(`${t("orderPlaced")} • $${data.total.toFixed(2)}`);
      await refresh();
      await refreshUser();
      navigate("/orders");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="checkout-page" className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-14">
      <h1 className="font-display text-3xl md:text-4xl mb-8">{t("checkout")}</h1>

      {cart.items.length === 0 ? (
        <div className="py-16 text-center text-[#767676]">
          {t("empty")}
          <div className="mt-4"><Link to="/shop" className="hover-underline">{t("continueShopping")}</Link></div>
        </div>
      ) : (
        <div className="grid gap-8">
          <div className="border border-[#E5E1D8] rounded-md p-6 bg-white">
            <div className="text-xs uppercase tracking-widest text-[#767676] mb-3">
              {lang === "ar" ? "الطلب" : "Order summary"}
            </div>
            {cart.items.map((it) => (
              <div key={it.product.id} className="flex justify-between py-2 text-sm">
                <span>{(lang === "ar" ? it.product.name_ar : it.product.name_en)} × {it.quantity}</span>
                <span>${it.line_total.toFixed(2)}</span>
              </div>
            ))}
            <div className="flex justify-between border-t border-[#E5E1D8] mt-3 pt-3 font-medium">
              <span>{t("total")}</span>
              <span data-testid="checkout-total">${cart.total.toFixed(2)}</span>
            </div>
          </div>

          <div className="border border-[#E5E1D8] rounded-md p-6 bg-white">
            <label className="text-xs uppercase tracking-widest text-[#767676]">{t("shippingAddress")}</label>
            <Input
              value={addr}
              onChange={(e) => setAddr(e.target.value)}
              className="mt-2 h-11 bg-white"
              data-testid="shipping-input"
              placeholder={lang === "ar" ? "المدينة، الشارع…" : "City, street…"}
            />
          </div>

          <div className="border border-[#E5E1D8] rounded-md p-6 bg-white">
            <div className="flex items-baseline justify-between">
              <span className="text-xs uppercase tracking-widest text-[#767676]">{t("walletBalance")}</span>
              <span className="font-medium">${user.wallet_balance.toFixed(2)}</span>
            </div>
            {insufficient && (
              <div className="mt-3 p-3 bg-[#F3F0EB] rounded text-sm text-[#7B5CBF]" data-testid="insufficient-msg">
                {t("insufficient")} <Link to="/wallet" className="hover-underline ms-2">{t("howToTopUp")}</Link>
              </div>
            )}
            <Button
              data-testid="pay-btn"
              disabled={insufficient || loading}
              onClick={submit}
              className="w-full mt-6 rounded-full bg-[#1A1738] hover:bg-[#2C2650] text-white h-11"
            >
              {loading ? t("loading") : `${t("payWithWallet")} • $${cart.total.toFixed(2)}`}
            </Button>
          </div>
        </div>
      )}
    </main>
  );
}
